import React, { useState, useRef, useEffect, useCallback } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download, Plus, Trash2, RefreshCw, Sheet } from "lucide-react";

export default function CSVUploadPage() {
  const [uploadType, setUploadType] = useState("clients"); // "clients" | "deposit_reports" | "withdraw_reports"
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const [sheetIdInput, setSheetIdInput] = useState("");
  const [importingSheets, setImportingSheets] = useState(false);

  // Deposit reports list state
  const [depositReports, setDepositReports] = useState([]);
  const [depositReportsTotal, setDepositReportsTotal] = useState(0);
  const [depositReportsPage, setDepositReportsPage] = useState(1);
  const [depositSearch, setDepositSearch] = useState("");
  const [loadingDepositReports, setLoadingDepositReports] = useState(false);

  // Withdraw reports list state
  const [withdrawReports, setWithdrawReports] = useState([]);
  const [withdrawReportsTotal, setWithdrawReportsTotal] = useState(0);
  const [withdrawReportsPage, setWithdrawReportsPage] = useState(1);
  const [withdrawSearch, setWithdrawSearch] = useState("");
  const [loadingWithdrawReports, setLoadingWithdrawReports] = useState(false);

  // ── Auto-fetch state ──────────────────────────────────────────────────────
  const [autoFetch, setAutoFetch] = useState(false);
  const [autoIntervalMin, setAutoIntervalMin] = useState("30");
  const [lastAutoSync, setLastAutoSync] = useState(null);
  const [autoSyncResult, setAutoSyncResult] = useState(null);
  const autoTimerRef = useRef(null);

  const fetchDepositReports = useCallback(async (page = 1, search = "") => {
    setLoadingDepositReports(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/deposit-reports?page=${page}&limit=50&search=${encodeURIComponent(search)}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) {
        setDepositReports(data.reports || []);
        setDepositReportsTotal(data.total || 0);
      }
    } catch (e) {
      console.error("Failed to fetch deposit reports:", e);
    } finally {
      setLoadingDepositReports(false);
    }
  }, []);

  const fetchWithdrawReports = useCallback(async (page = 1, search = "") => {
    setLoadingWithdrawReports(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/withdraw-reports?page=${page}&limit=50&search=${encodeURIComponent(search)}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) {
        setWithdrawReports(data.reports || []);
        setWithdrawReportsTotal(data.total || 0);
      }
    } catch (e) {
      console.error("Failed to fetch withdraw reports:", e);
    } finally {
      setLoadingWithdrawReports(false);
    }
  }, []);

  useEffect(() => {
    if (uploadType === "deposit_reports") {
      fetchDepositReports(depositReportsPage, depositSearch);
    } else if (uploadType === "withdraw_reports") {
      fetchWithdrawReports(withdrawReportsPage, withdrawSearch);
    }
  }, [uploadType, depositReportsPage, depositSearch, withdrawReportsPage, withdrawSearch, fetchDepositReports, fetchWithdrawReports]);

  const runSheetImport = useCallback(async (sheetId = "") => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sheets/import`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ sheet_id: sheetId }),
      credentials: "include",
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || "Sheets import failed");
    return data;
  }, []);

  // Start / stop auto-fetch interval
  useEffect(() => {
    if (autoFetch) {
      const intervalMs = parseInt(autoIntervalMin, 10) * 60 * 1000;
      runSheetImport(sheetIdInput)
        .then(data => { setLastAutoSync(new Date()); setAutoSyncResult(data); toast.success(`Auto-sync: ${data.created} created, ${data.updated} updated`); })
        .catch(err => toast.error(`Auto-sync failed: ${err.message}`));
      autoTimerRef.current = setInterval(() => {
        runSheetImport(sheetIdInput)
          .then(data => { setLastAutoSync(new Date()); setAutoSyncResult(data); })
          .catch(err => console.error("Auto-sync error:", err.message));
      }, intervalMs);
    } else {
      clearInterval(autoTimerRef.current);
    }
    return () => clearInterval(autoTimerRef.current);
  }, [autoFetch, autoIntervalMin]);

  const handleGoogleSheetsImport = async () => {
    setImportingSheets(true);
    setResult(null);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sheets/import`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          sheet_id: sheetIdInput
        }),
        credentials: "include"
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Sheets import failed");
      setResult(data);
      toast.success(`Sheets processed: ${data.created} created, ${data.updated} updated`);
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setImportingSheets(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith(".csv")) setFile(f);
    else toast.error("Please upload a CSV file");
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Select a file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("token");
      const endpoint = uploadType === "deposit_reports" 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/upload/deposit-report-csv`
        : uploadType === "withdraw_reports"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/upload/withdraw-report-csv`
        : `${process.env.REACT_APP_BACKEND_URL}/api/upload/csv`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
        credentials: "include"
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Upload failed");
      setResult(data);
      toast.success(`CSV processed: ${data.created} created, ${data.updated} updated`);
      if (uploadType === "deposit_reports") {
        fetchDepositReports(1, depositSearch);
      } else if (uploadType === "withdraw_reports") {
        fetchWithdrawReports(1, withdrawSearch);
      }
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSample = () => {
    const token = localStorage.getItem("token");
    const endpoint = uploadType === "deposit_reports"
      ? `${process.env.REACT_APP_BACKEND_URL}/api/upload/sample-deposit-report-csv`
      : uploadType === "withdraw_reports"
      ? `${process.env.REACT_APP_BACKEND_URL}/api/upload/sample-withdraw-report-csv`
      : `${process.env.REACT_APP_BACKEND_URL}/api/upload/sample-csv`;
    const filename = uploadType === "deposit_reports" 
      ? "sample_deposit_reports.csv" 
      : uploadType === "withdraw_reports"
      ? "sample_withdraw_reports.csv"
      : "sample_clients.csv";

    fetch(endpoint, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(err => toast.error("Failed to download sample CSV"));
  };

  return (
    <div className="space-y-6" data-testid="csv-upload-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>CSV Upload</h2>
          <p className="text-sm text-muted-foreground mt-1">Upload client data, deposit reports, or withdraw reports from a CSV file</p>
        </div>

        {/* Upload Mode Selector */}
        <div className="flex flex-wrap items-center gap-1.5 p-1 bg-muted/60 rounded-lg border border-border">
          <Button
            variant={uploadType === "clients" ? "default" : "ghost"}
            size="sm"
            onClick={() => { setUploadType("clients"); setFile(null); setResult(null); }}
            className="text-xs h-8"
            data-testid="upload-mode-clients"
          >
            Client Data CSV
          </Button>
          <Button
            variant={uploadType === "deposit_reports" ? "default" : "ghost"}
            size="sm"
            onClick={() => { setUploadType("deposit_reports"); setFile(null); setResult(null); }}
            className="text-xs h-8"
            data-testid="upload-mode-deposit-reports"
          >
            Deposit Reports CSV
          </Button>
          <Button
            variant={uploadType === "withdraw_reports" ? "default" : "ghost"}
            size="sm"
            onClick={() => { setUploadType("withdraw_reports"); setFile(null); setResult(null); }}
            className="text-xs h-8"
            data-testid="upload-mode-withdraw-reports"
          >
            Withdraw Reports CSV
          </Button>
        </div>
      </div>

      <div className="flex justify-start">
        <Button variant="outline" size="sm" onClick={handleDownloadSample} data-testid="download-sample-csv-btn">
          <Download className="w-4 h-4 mr-2" />
          {uploadType === "deposit_reports"
            ? "Download Sample Deposit Report CSV"
            : uploadType === "withdraw_reports"
            ? "Download Sample Withdraw Report CSV"
            : "Download Sample Client CSV"}
        </Button>
      </div>

      {/* Upload Area */}
      <Card className="border border-border">
        <CardContent className="p-8">
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${dragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="csv-drop-zone"
          >
            <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm font-medium mb-1">
              {file ? file.name : `Drop your ${uploadType === "deposit_reports" ? "Deposit Reports" : uploadType === "withdraw_reports" ? "Withdraw Reports" : "Client Data"} CSV file here or click to browse`}
            </p>
            <p className="text-xs text-muted-foreground">
              {uploadType === "deposit_reports"
                ? "Supports .csv with columns: User Id, Loginname, Branchname, Amount, UTR, Request Time, Approval Time, Payment Bank, Approve Delay (In Mins), Delay Group, Remark"
                : uploadType === "withdraw_reports"
                ? "Supports .csv with columns: User Id, Loginname, Branchname, Amount, UTR, Request Time, Approval Time, Delay, Delay Group, Remark"
                : "Supports .csv files with Indian mobile numbers (10 digits starting with 6-9)"}
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={e => e.target.files[0] && setFile(e.target.files[0])}
              data-testid="csv-file-input"
            />
          </div>

          {file && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setFile(null); setResult(null); }} data-testid="clear-file-btn">Clear</Button>
                <Button size="sm" onClick={handleUpload} disabled={uploading} data-testid="upload-csv-btn">
                  {uploading ? "Processing..." : `Upload & Process ${uploadType === "deposit_reports" ? "Deposit Reports" : uploadType === "withdraw_reports" ? "Withdraw Reports" : "Clients"}`}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4" data-testid="upload-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Created</p>
                  <p className="text-xl font-bold">{result.created}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Updated</p>
                  <p className="text-xl font-bold">{result.updated}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <XCircle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Skipped</p>
                  <p className="text-xl font-bold">{result.skipped}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-border">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                  <p className="text-xl font-bold">{result.total_processed}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Errors Table */}
          {result.errors && result.errors.length > 0 && (
            <Card className="border border-destructive/50">
              <CardContent className="p-0">
                <div className="p-4 border-b border-border">
                  <p className="text-sm font-semibold text-destructive">Errors ({result.errors.length})</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase">Row</TableHead>
                      <TableHead className="text-xs uppercase">User ID</TableHead>
                      <TableHead className="text-xs uppercase">Target</TableHead>
                      <TableHead className="text-xs uppercase">Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.errors.map((err, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{err.row}</TableCell>
                        <TableCell className="text-sm">{err.user_id || "-"}</TableCell>
                        <TableCell className="text-sm font-mono">{err.mobile || err.utr || "-"}</TableCell>
                        <TableCell className="text-sm text-destructive">{err.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Google Sheets Import (Only on Clients Mode) */}
      {uploadType === "clients" && (
        <Card className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Google Sheets Import
              {autoFetch && (
                <span className="ml-auto flex items-center gap-1.5 text-[10px] text-green-600 font-normal normal-case">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  Auto-fetching every {autoIntervalMin} min
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Google Sheet ID or URL (Leave empty to use Settings default)"
                  value={sheetIdInput}
                  onChange={e => setSheetIdInput(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <Button
                size="sm"
                onClick={handleGoogleSheetsImport}
                disabled={importingSheets}
                className="h-8 flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${importingSheets ? "animate-spin" : ""}`} />
                {importingSheets ? "Syncing..." : "Sync Now"}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-4 p-3 rounded-lg bg-muted/50 border border-border">
              <div className="flex items-center gap-2">
                <Switch
                  id="auto-fetch-switch"
                  checked={autoFetch}
                  onCheckedChange={setAutoFetch}
                />
                <Label htmlFor="auto-fetch-switch" className="text-sm font-medium cursor-pointer">
                  Auto Fetch
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Interval:</Label>
                <Select value={autoIntervalMin} onValueChange={setAutoIntervalMin} disabled={autoFetch}>
                  <SelectTrigger className="h-7 w-24 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 min</SelectItem>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {lastAutoSync && (
                <span className="text-[10px] text-muted-foreground ml-auto">
                  Last sync: {lastAutoSync.toLocaleTimeString()}
                  {autoSyncResult && (
                    <> &mdash; <span className="text-green-600">{autoSyncResult.created} created</span>, <span className="text-blue-600">{autoSyncResult.updated} updated</span></>
                  )}
                </span>
              )}
            </div>

            <p className="text-[10px] text-muted-foreground">
              Ensure your sheet has columns matching: <code>user_id</code>, <code>mobile_no</code>, <code>username</code>, <code>total_deposit</code>, <code>total_withdraw</code>, <code>site</code>, <code>registration_date</code>.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Format Guide Section */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {uploadType === "deposit_reports"
              ? "Deposit Reports CSV Format Guide"
              : uploadType === "withdraw_reports"
              ? "Withdraw Reports CSV Format Guide"
              : "Client Data CSV Format Guide"}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {uploadType === "deposit_reports"
              ? "Ensure your deposit report CSV file includes all 11 required columns"
              : uploadType === "withdraw_reports"
              ? "Ensure your withdraw report CSV file includes all 10 required columns"
              : "Same columns as Chrome Extension & Google Sheets — all fields supported"}
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full border-collapse text-xs" style={{ fontFamily: 'Google Sans, Roboto, sans-serif' }}>
              <thead>
                <tr>
                  <th className="border border-[#c6c6c6]/40 text-center w-8 py-1 px-2 text-[10px] font-normal" style={{ background: '#f3f3f3', color: '#444' }}></th>
                  {(uploadType === "deposit_reports"
                    ? ["User Id", "Loginname", "Branchname", "Amount", "UTR", "Request Time", "Approval Time", "Payment Bank", "Approve Delay (In Mins)", "Delay Group", "Remark"]
                    : uploadType === "withdraw_reports"
                    ? ["User Id", "Loginname", "Branchname", "Amount", "UTR", "Request Time", "Approval Time", "Delay", "Delay Group", "Remark"]
                    : ["user_id", "mobile_no", "username", "total_deposit", "total_withdraw", "branch", "site", "registration_date", "last_deposit", "deposit_count"]
                  ).map((col) => (
                    <th
                      key={col}
                      className="border border-[#c6c6c6]/60 py-1.5 px-3 font-semibold text-left whitespace-nowrap"
                      style={{
                        background: uploadType === "deposit_reports" ? '#2563eb' : uploadType === "withdraw_reports" ? '#dc2626' : '#0f9d58',
                        color: '#fff',
                        letterSpacing: '0.02em'
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(uploadType === "deposit_reports"
                  ? [
                      ["P-7646463", "john_doe",   "Main Branch", "5000", "UTR98765432101", "2026-02-23 10:15:00", "2026-02-23 10:18:00", "HDFC Bank", "3",  "0-5 Mins",  "Verified deposit"],
                      ["P-645454",  "jane_smith", "North Branch", "2500", "UTR87654321092", "2026-02-23 11:00:00", "2026-02-23 11:12:00", "ICICI Bank", "12", "10-15 Mins", "Auto approved"],
                    ]
                  : uploadType === "withdraw_reports"
                  ? [
                      ["P-7646463", "john_doe",   "Main Branch", "1200", "WDR98765432101", "2026-02-23 14:10:00", "2026-02-23 14:15:00", "5 Mins",  "0-5 Mins",  "Withdrawal processed"],
                      ["P-645454",  "jane_smith", "North Branch", "800",  "WDR87654321092", "2026-02-23 15:20:00", "2026-02-23 15:30:00", "10 Mins", "10-15 Mins", "Approved by admin"],
                    ]
                  : [
                      ["USR001", "9876543210", "john_doe",  "5000", "1200", "Main Branch",  "site1@gmail.com", "2025-01-15", "2026-02-10", "5"],
                      ["USR002", "8765432109", "jane_smith","0",    "0",    "North Branch", "Site 2",          "2025-03-20", "",           "0"],
                    ]
                ).map((row, ri) => (
                  <tr
                    key={ri}
                    style={{ background: ri % 2 === 0 ? 'hsl(var(--card))' : 'hsl(var(--muted)/0.3)' }}
                    className="hover:brightness-95 transition-all"
                  >
                    <td className="border border-[#c6c6c6]/30 text-center py-1 px-2 text-[10px] select-none" style={{ background: '#f3f3f3', color: '#888' }}>{ri + 1}</td>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className="border border-[#c6c6c6]/30 py-1.5 px-3 font-mono whitespace-nowrap"
                        style={{ color: cell === '' ? '#aaa' : undefined }}
                      >
                        {cell === '' ? <span className="italic text-muted-foreground">(empty)</span> : cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {uploadType === "deposit_reports" ? (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-xs text-muted-foreground">
              <p>- <strong>User Id</strong>: Client User ID (e.g. P-7646463).</p>
              <p>- <strong>Loginname</strong>: Client login username.</p>
              <p>- <strong>Branchname</strong>: Operational branch name.</p>
              <p>- <strong>Amount</strong>: Deposit amount (₹).</p>
              <p>- <strong>UTR</strong>: Unique Transaction Reference number.</p>
              <p>- <strong>Request Time</strong>: Timestamp when deposit was requested.</p>
              <p>- <strong>Approval Time</strong>: Timestamp when deposit was approved.</p>
              <p>- <strong>Payment Bank</strong>: Bank name used for deposit payment.</p>
              <p>- <strong>Approve Delay (In Mins)</strong>: Delay duration in minutes.</p>
              <p>- <strong>Delay Group</strong>: Grouping category for approval delay (e.g. 0-5 Mins).</p>
              <p>- <strong>Remark</strong>: Additional notes or remarks.</p>
            </div>
          ) : uploadType === "withdraw_reports" ? (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-xs text-muted-foreground">
              <p>- <strong>User Id</strong>: Client User ID (e.g. P-7646463).</p>
              <p>- <strong>Loginname</strong>: Client login username.</p>
              <p>- <strong>Branchname</strong>: Operational branch name.</p>
              <p>- <strong>Amount</strong>: Withdrawal amount (₹).</p>
              <p>- <strong>UTR</strong>: Unique Transaction Reference / Payout number.</p>
              <p>- <strong>Request Time</strong>: Timestamp when withdrawal was requested.</p>
              <p>- <strong>Approval Time</strong>: Timestamp when withdrawal was approved.</p>
              <p>- <strong>Delay</strong>: Approval delay time string (e.g. 5 Mins).</p>
              <p>- <strong>Delay Group</strong>: Delay grouping category (e.g. 0-5 Mins).</p>
              <p>- <strong>Remark</strong>: Additional notes or remarks.</p>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-xs text-muted-foreground">
              <p>- <strong>user_id</strong>: Unique ID. Duplicates auto-update existing records.</p>
              <p>- <strong>mobile_no</strong>: 10-digit Indian number (starts with 6–9).</p>
              <p>- <strong>username</strong>: Display name / login username.</p>
              <p>- <strong>total_deposit</strong>: Cumulative deposit amount (₹).</p>
              <p>- <strong>total_withdraw</strong>: Cumulative withdrawal amount (₹).</p>
              <p>- <strong>branch</strong>: Operational branch name (e.g. Main Branch, North Branch).</p>
              <p>- <strong>site</strong>: Platform / site name (e.g. site1@gmail.com, Site 2).</p>
              <p>- <strong>registration_date</strong>: Client registration date (YYYY-MM-DD).</p>
              <p>- <strong>last_deposit</strong>: Date of last deposit. Empty = New. Within 2 days = Hot.</p>
              <p>- <strong>deposit_count</strong>: Total number of deposits made.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deposit Reports Live Data Table */}
      {uploadType === "deposit_reports" && (
        <Card className="border border-border">
          <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Uploaded Deposit Reports ({depositReportsTotal})
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Live list of processed deposit report records</p>
            </div>
            <Input
              placeholder="Search User ID, Login, UTR, Bank..."
              value={depositSearch}
              onChange={e => { setDepositSearch(e.target.value); setDepositReportsPage(1); }}
              className="h-8 w-64 text-xs"
            />
          </CardHeader>
          <CardContent>
            {loadingDepositReports ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Loading deposit reports...</div>
            ) : depositReports.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">No deposit reports uploaded yet.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase">User ID</TableHead>
                      <TableHead className="text-xs uppercase">Loginname</TableHead>
                      <TableHead className="text-xs uppercase">Branch</TableHead>
                      <TableHead className="text-xs uppercase">Amount (₹)</TableHead>
                      <TableHead className="text-xs uppercase">UTR</TableHead>
                      <TableHead className="text-xs uppercase">Req. Time</TableHead>
                      <TableHead className="text-xs uppercase">Appr. Time</TableHead>
                      <TableHead className="text-xs uppercase">Bank</TableHead>
                      <TableHead className="text-xs uppercase">Delay (min)</TableHead>
                      <TableHead className="text-xs uppercase">Delay Group</TableHead>
                      <TableHead className="text-xs uppercase">Remark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {depositReports.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-semibold">{r.user_id || "-"}</TableCell>
                        <TableCell className="text-xs">{r.loginname || "-"}</TableCell>
                        <TableCell className="text-xs">{r.branchname || "-"}</TableCell>
                        <TableCell className="text-xs font-mono font-medium text-green-600">₹{r.amount || 0}</TableCell>
                        <TableCell className="text-xs font-mono">{r.utr || "-"}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.request_time || "-"}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.approval_time || "-"}</TableCell>
                        <TableCell className="text-xs">{r.payment_bank || "-"}</TableCell>
                        <TableCell className="text-xs font-mono">{r.approve_delay_in_mins ?? "-"}</TableCell>
                        <TableCell className="text-xs">{r.delay_group || "-"}</TableCell>
                        <TableCell className="text-xs">{r.remark || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Withdraw Reports Live Data Table */}
      {uploadType === "withdraw_reports" && (
        <Card className="border border-border">
          <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Uploaded Withdraw Reports ({withdrawReportsTotal})
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Live list of processed withdrawal report records</p>
            </div>
            <Input
              placeholder="Search User ID, Login, UTR..."
              value={withdrawSearch}
              onChange={e => { setWithdrawSearch(e.target.value); setWithdrawReportsPage(1); }}
              className="h-8 w-64 text-xs"
            />
          </CardHeader>
          <CardContent>
            {loadingWithdrawReports ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Loading withdraw reports...</div>
            ) : withdrawReports.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">No withdraw reports uploaded yet.</div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs uppercase">User ID</TableHead>
                      <TableHead className="text-xs uppercase">Loginname</TableHead>
                      <TableHead className="text-xs uppercase">Branch</TableHead>
                      <TableHead className="text-xs uppercase">Amount (₹)</TableHead>
                      <TableHead className="text-xs uppercase">UTR</TableHead>
                      <TableHead className="text-xs uppercase">Req. Time</TableHead>
                      <TableHead className="text-xs uppercase">Appr. Time</TableHead>
                      <TableHead className="text-xs uppercase">Delay</TableHead>
                      <TableHead className="text-xs uppercase">Delay Group</TableHead>
                      <TableHead className="text-xs uppercase">Remark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withdrawReports.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-semibold">{r.user_id || "-"}</TableCell>
                        <TableCell className="text-xs">{r.loginname || "-"}</TableCell>
                        <TableCell className="text-xs">{r.branchname || "-"}</TableCell>
                        <TableCell className="text-xs font-mono font-medium text-red-600">₹{r.amount || 0}</TableCell>
                        <TableCell className="text-xs font-mono">{r.utr || "-"}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.request_time || "-"}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.approval_time || "-"}</TableCell>
                        <TableCell className="text-xs font-mono">{r.delay || "-"}</TableCell>
                        <TableCell className="text-xs">{r.delay_group || "-"}</TableCell>
                        <TableCell className="text-xs">{r.remark || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
}
